<template>
	<row-object-page name="contact_edit" :force-info="true" :oref="oref" v-model="contact" :buttons="buttons" :add-save="false" :add-save-and-back="false"></row-object-page>
	<Teleport to="body">
		<modal :show="showModal" @close="showModal = false">
			<template #body>
				<replan-date :subject="contact.subject" @replan="replan"/>
			</template>
		</modal>
	</Teleport>
</template>

<script setup lang="ts">
//[<Button>{ type: "button", label: env.msg('butt.continue'), action: "continue", feedback: true }]
//['update']
	
import {ref, computed, provide, onUnmounted, reactive} from "vue";
import RowObjectPage from "@/pages/row-object/RowObjectPage.vue";
import ReplanDate from "@/vue/agenda/ReplanDate.vue";
import Modal from '@/vue/modal/Modal.vue';
import {objectRef, RowObjectInit, RowObjectRef, RowObjectType, SrcType} from "@t/meta/SourceType.ts";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import {NEW_RECS_GROUP_ID, PRINTS_GROUP_ID, SideMenu, TOOLS_GROUP_ID} from "@t/ui/Menu.ts";
import type {Contact} from "@t/contact/Contact.ts";
import {connectEventBus, injectEnv} from "@fd/Environment.ts";
import {hasAccess} from "@t/security/ObjectAccess.ts";
import {Button, ButtonProps} from "@t/fd/fixed/Button.ts";
import type {FormPartOrReference} from "@t/fd/FormPartOrReference.ts";
import {go} from "@/router/Router.ts";
import {addContextParams, TASK_REFS, CONTACT_REFS, EVENT_REFS} from "@t/fd/AppContext.ts";
import {parseDate, format, formatDate} from "@t/util/DateUtil";
import type {ParsedDate} from "@t/util/DateUtil";
import {useVirtualRoute} from "@/router/virtual/VirtualRouter.ts";
import {CREATE, EDIT, REPLACE_INIT_WITH_EDIT} from "@/pages/row-object/RowObjectEvents.ts";
import {standardNewRecordGroup} from "@/store/MenuBuilder.ts";

const ns = useNavigationStore();
const env = injectEnv();
const bus = connectEventBus("ContactPage");

const contact = ref<Contact>(null);
const rowObject = ref<InstanceType<typeof RowObjectPage> | null>(null);
const showModal = ref(false);

const route = useVirtualRoute();
const oref = objectRef(route, SrcType.CONTACT);

const buttons = computed<FormPartOrReference[]>(() => {
	
	var saveButt : Button = { type: "button", mutating: true, label: env.msg('butt.save'), action: "save", feedback: true };
	var saveBackButt : Button = { type: "button", mutating: true, label: env.msg('butt.saveAndBack'), action: "saveAndBack", feedback: true };
	
	saveBackButt.more = [<ButtonProps>{ label: env.msg('markDone.plan') },
				<ButtonProps>{ label: env.msg('markDone.newCall'), action: "newCall", icon: "phone" },
				<ButtonProps>{ label: env.msg('markDone.newEvent'), action: "newEvent", icon: "event" },
				<ButtonProps>{ label: env.msg('markDone.newTask'), action: "newTask", icon: "task" } 
			];
	return [saveButt, saveBackButt];
});


bus.onCommand(["newCall","newEvent","newTask"], (cmd:string) => {
	if (!contact.value.contacted) {
		contact.value.contacted=formatDate(new Date(), true, false);
	}
	return new Promise(async (resolve) => {
		bus.executeCommand("saveOnly").then((success)=>{
			if (success) {//if save is o.k.
				if (cmd === "newTask") {
					let link = addContextParams('createTask.do', rowObject.value.getDialogContext().value, TASK_REFS);
					link = env.net.urlParam(link, "parentContact", contact.value.id);
					//console.log("link:"+link);
					go(link);
				}
				else if (cmd === "newCall") {
					let link = 'createContact.do';
					link = env.net.urlParam(link, "parentContact", contact.value.id);
					link = env.net.urlParam(link, "days", 1);
					link = env.net.urlParam(link, "contactType", 1);
					link = env.net.urlParam(link, "replanContact", "true");
					//console.log("link:"+link);
					go(link);
				}
				else if (cmd === "newEvent") {
					let link = addContextParams('planEvent.do', rowObject.value.getDialogContext().value, EVENT_REFS);
					link = env.net.urlParam(link, "parentContact", contact.value.id);
					//console.log("link:"+link);
					go(link);
				}
				resolve("ok");
			}
			else {
				resolve("fail");
			}
		})
		.catch((error) => {
			console.error("Error, stop nex step:", error);
		});
	});
});

ns.listenForStackerUpdates(bus, oref, () => env.msg('title.createContact'),
	"/editContact.do", () => contact.value.subject, () => contact.value.id);

ns.defineMenu((mb) => {
	if (contact.value?.id) {
		mb.pageNamed("contact_edit");
		mb.inGroup(NEW_RECS_GROUP_ID, standardNewRecordGroup());
		mb.inGroup(TOOLS_GROUP_ID, (gb) => {
			gb.navItem("seeThis", "see.do?id="+contact.value?.id+"&type="+SrcType.CONTACT, "menu.seeThis", "eye-eyebrow");
			if (contact.value?.id && hasAccess(contact.value, "delete")) {
				if (!contact.value.contacted) {
					gb.executeItem("moveContact", () => { showModal.value = true; }, "menu.moveContact", "replan");
				}
				gb.confirmDelete(gb.navItem("deleteContact", "deleteContact.do?id="+contact.value.id, "title.deleteContact", "trash"), "menu.deleteContact.confirm");
			}
		});
		mb.inGroup(PRINTS_GROUP_ID, (gb) => {
			gb.navItem("outputParams", "outputParams.do?descriptor=6&pk=" + contact.value.id + "&section="+ns.activeSection, "menu.printContact", "printer");
		});
	}
});

function replan(reason: string, localDateIso: string): void {
	//console.log("accepted replan values:"+reason+","+localDateIso+" original:"+contact.value.planContact);
	bus.fire("flush", "description");

	const origPlanContact = parseDate(contact.value.planContact);
	var today = <ParsedDate>{date: new Date(), wholeDay: false};
	contact.value.planContact=localDateIso;

	var reasonText = "<blockquote><p class='quote_header'>"
		+ env.msg("contact.replan.original")+": "+ format(origPlanContact,env)+"<br>"
		+ env.msg("contact.replan.day")+": "+format(today,env)+"<br>"
		+ env.msg("contact.reason")+": "+reason
		+ "</p></blockquote>";

	contact.value.description = reasonText + (contact.value.description ? contact.value.description : "");
	
	bus.fire("update", "description");
	showModal.value = false;
}

function replanDialog(): void {
	showModal.value = !showModal.value;
}
</script>

<style>

</style>
